import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import {
  Button,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  Divider,
  CardHeader
} from '@mui/material';

import { useNotification } from 'config/Notification';

import { getAllUsersService, userActivateService, userDeactivateService } from 'services/modules/idp/user';

const UsersListECS = () => {
  // State  variables
  const [userData, setUserData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalResults, setTotalResults] = useState(0);

  // Redux selectors
  const authenticatedUserContainer = useSelector((state) => state.auth.user);
  const companyDataContainer = useSelector(state => state.auth.companyData);
  const loginIdContainer = useSelector((state) => state.auth.user.id);

  // React Router
  const navigate = useNavigate();

  // Notification 
  const { openNotification } = useNotification();

  // Handles page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handles change in search query like username
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (companyDataContainer?.companyid !== null)
      fetchUsersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDataContainer, rowsPerPage, searchQuery, page]);

  // Method to fetch the users data based on company id
  const fetchUsersData = async () => {
    try {
      const params = {
        company: companyDataContainer?.companyid,
        sortBy: '-createdAt',
        limit: rowsPerPage,
        page: page + 1,
      };

      if (searchQuery.trim() !== '')
        params.search = searchQuery;
      const response = await getAllUsersService(params);
      setUserData(response?.results);
      setTotalResults(response?.totalResults);
    } catch (error) {
      openNotification('Server error', error);
    }
  };

  // Handles navigation to profile component
  const handleViewProfile = (userId) => {
    navigate(`/profile/${userId}`);
  };

  // Handles navigation to add user component
  const handleAdduser = () => {
    navigate(`/add-user/${companyDataContainer?.companyid}`);
  };

  // Handles navigation to update profile component
  const handeUpdateProfile = (userId) => {
    navigate(`/update-profile/${userId}`);
  };

  // Handles drop down change for activating or deactivating the user
  const handleDropdownChange = async (e, userId) => {
    try {
      if (!e || !e.target) {
        return;
      }

      if (e.target.value === "INACTIVE") {
        let response = await userDeactivateService({ userId });
        if (response !== 200) {
          openNotification('Deactivated failed');
        } else {
          fetchUsersData();
          openNotification('Deactivated successfully');
        }
      } else {
        let response = await userActivateService({ userId });
        if (response !== 200) {
          openNotification('Activated failed');
        } else {
          fetchUsersData();
          openNotification('Activated successfully');
        }
      }
    } catch (error) {
      console.error('Error handling dropdown change:', error);
    }
  };

  return (
    <div className='card-container'>
      <div className='users-card p-2 full-width-field'>
        <CardHeader title={
          <React.Fragment>
            <div className='card-head'>
              <div className="flex-head">
                <img src="/assets/groupsvc.5fc77efe.jpg" alt="icon" className="card-icon" />
                <label className='label'> Users</label>
              </div>
              {(authenticatedUserContainer &&
                authenticatedUserContainer.roles &&
                authenticatedUserContainer.roles.some((role) =>
                  role.permissions.includes('CREATE_CHILD_USERS')
                )
              ) && (
                  <div><Button onClick={() => handleAdduser()} variant="contained">+ Add new user</Button></div>
                )}
            </div>
          </React.Fragment>
        } />

        <Divider className='divider' />

        <div className="card-bottom-container">
          <div className='flex-card  d-flex align-items-center justify-content-between'>
            <div className="m-1">
              <TextField
                variant="outlined"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div>
              <Button
                variant="contained"
                className="back font mt-3"
                // onClick={() => window.history.back()}
                onClick={() => navigate('/companies')}
              >
                Back
              </Button>
            </div>
          </div>

          <div>
            <TableContainer component={Paper}>
              <Table>
                <TableHead className="table-head">
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Created on</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userData === null ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : userData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center"><ReportProblemIcon /> No user data available</TableCell>
                    </TableRow>
                  ) : (
                    userData.map((user) => (
                      <TableRow className="table-row" key={user.id}>
                        <TableCell className="name-table">
                          <AccountCircleRoundedIcon className="user-icon-table" /> {user.name}{' '}
                          <Tooltip title="Admin">
                            {user.roles && user.roles.some(role => role.slug === 'admin') && (
                              <VerifiedUserRoundedIcon className="admin-icon-table" />
                            )}
                          </Tooltip>
                          <Tooltip title="Super admin">
                            {user.roles && user.roles.some(role => role.slug === 'super-admin') && (
                              <AdminPanelSettingsIcon className="admin-icon-table" />
                            )}
                          </Tooltip>
                          <Tooltip title="Analyst">
                            {user.roles && user.roles.some(role => role.slug === 'analyst') && (
                              <PersonSearchIcon className='analyst-icon-table' />
                            )}
                          </Tooltip>
                        </TableCell>
                        <TableCell className="role-table">{user.username}</TableCell>
                        <TableCell className="role-table">{user.roles.map(role => role.label)}</TableCell>
                        <TableCell className="table-text">{user.company.name}</TableCell>
                        <TableCell className="table-email">{user.email} <CheckCircleIcon className="use-verication-icon-table" /> </TableCell>
                        <TableCell className="table-text">{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Select variant="standard" className={`status-dropdown ${user.status === 'ACTIVE' ? 'active-status' : 'inactive-status'}`} defaultValue={user.status} disabled={loginIdContainer === user.id ? true : false} onChange={(e) => handleDropdownChange(e, user.id)}>
                            <MenuItem value="ACTIVE" className={user.status === 'ACTIVE' ? 'active-item' : 'not-active-item'}>Active</MenuItem>
                            <MenuItem value="INACTIVE" className={user.status === 'INACTIVE' ? 'active-item' : 'inactive-item'} >InActive</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View User" placement="top">
                            <img src="/assets/viewicon.svg" alt="View" className='icons-style' onClick={() => handleViewProfile(user.id)} />
                          </Tooltip>
                          {(authenticatedUserContainer &&
                            authenticatedUserContainer.roles &&
                            authenticatedUserContainer.roles.some((role) =>
                              role.permissions.includes('UPDATE_SELF_USERS')
                            )
                          ) && (
                              <Tooltip title="Edit User" placement="top">
                                <div className="edit-container" onClick={() => handeUpdateProfile(user.id)}>
                                  <EditIcon className='usersandediticon' />
                                </div>
                              </Tooltip>
                            )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
                component="div"
                count={totalResults}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersListECS;
